/* eslint-disable @next/next/no-html-link-for-pages */
import { zodResolver } from "@hookform/resolvers/zod";
import { GetServerSideProps } from "next";
import { signIn, useSession } from "next-auth/react";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { CountryNavbarComponent } from "../components/Auth/CountryDropdown/CountryDropdown";
import { AuthLoginComponent } from "../components/Auth/Login/Login";
import {
  LoginFormSchema,
  LoginFormSchemaType,
} from "../components/Auth/Login/LoginFormSchema";
import { AuthOtpComponent } from "../components/Auth/Otp/Otp";
import {
  OtpFormSchema,
  OtpFormSchemaType,
} from "../components/Auth/Otp/OtpFormSchema";
import AuthLayout from "../components/Layouts/Auth/Auth";
import { authenticateUserController } from "../lib/controllers/auth/authenticateUser";
import { authGetOtpController } from "../lib/controllers/auth/otp/get";
import getCampaign from "../lib/controllers/campaign/getCampaign";
import { renew2022CampaignData } from "../lib/data/campaign/renew2022";
import { useSegmentAnalytics } from "../lib/hooks/useSegmentAnalytics";
import useSessionStorage from "../lib/hooks/useSessionStorage";

import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import * as Sentry from "@sentry/nextjs";
import _ from "lodash";
import { PrimeLink } from "../components/prime/link";
import { getIpCountry } from "../lib/helper/getIpCountry";
import { user } from "../lib/types/next-auth";
import tenantConfig from "../tenant.config";
import { featureFlag } from "../lib/featureFlags/featureFlags";
import { useToast } from "@alta/admin";

export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  const { campaign, callbackUrl, callbackCountry } = query;
  const country: "SG" | "MY" =
    callbackCountry === "SG"
      ? "SG"
      : callbackCountry === "MY"
        ? "MY"
        : getIpCountry(req);
  //If theres country code, return them as props
  // If there's campaign code grab relevant campaign data and return them
  if (campaign) {
    const campaignData = getCampaign(campaign as string);
    if (campaignData) {
      return {
        props: {
          title: campaignData.login?.title,
          info: campaignData.login?.info,
          campaignCode: campaignData.data.code,
          callbackUrl: callbackUrl || null,
          countryCode: country || null,
        },
      };
    } else {
      return {
        redirect: {
          permanent: false,
          destination: "/signup",
        },
      };
    }
  }
  return {
    props: {
      callbackUrl: callbackUrl || null,
      countryCode: country || null,
    },
  };
};

/**************************************************
 *
 * View starts here.
 *
 **************************************************/

const IS_RECAPTCHA_ENABLED = featureFlag("recaptcha");

function LoginPage(props: {
  // For redirecting user back to previous page after login
  callbackUrl?: string;

  // For completing login
  email?: string;

  // For displaying campaign-related data
  campaignCode?: string;
  title?: string;
  info?: string;
  // For country-specific views
  countryCode?: string;
  sessionCountry: string;
}) {
  const [email, setEmail] = useState<string>("");
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isLoginSuccess, setIsLoginSuccess] = useState<boolean>(false);
  const [shouldShowOtpForm, setShouldShowOtpForm] = useState<boolean>(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const useLoginFormProps = useForm<LoginFormSchemaType>({
    resolver: zodResolver(LoginFormSchema),
  });
  const useOtpFormProps = useForm<OtpFormSchemaType>({
    resolver: zodResolver(OtpFormSchema),
  });
  const { info, callbackUrl } = props;
  const router = useRouter();
  const { segmentAnalytics } = useSegmentAnalytics();
  const session = useSession();
  const { setItem, getItem } = useSessionStorage();
  //to wait for country useEffect to load
  const [isLoading, setLoading] = useState<boolean>(true);
  const [currentCountry, setCurrentCountry] = useState<string>("");
  const { toast } = useToast();

  useEffect(() => {
    if (router.query.msg === "session_expired") {
      toast({
        title: "Session expired",
        description: "Please login again to access the platform.",
        variant: "default",
      });
    }
  }, [router.query])

  useEffect(() => {
    const sessionCountry = getItem("selectedCountry");
    if (sessionCountry) {
      setCurrentCountry(sessionCountry);
    } else {
      setCurrentCountry(props?.countryCode === "MY" ? "MY" : "SG");
    }
    setLoading(false);
  }, [props.countryCode]);
  const setCountry = (selectedCountry: string) => {
    setCurrentCountry(selectedCountry);
    setShouldShowOtpForm(false);
    useLoginFormProps.reset();
    setItem("selectedCountry", selectedCountry);
  };

  async function identifyUserOnSegment(user: user) {
    await segmentAnalytics.identify(user.userId, {
      createdAt: new Date(),
      userId: user.userId,
      traderId: user.traderId,
      traderType: user.traderType,
      accreditationStatus: user.accreditationStatus,
      kycVerificationStatus: user.kycVerificationStatus,
    });

    await segmentAnalytics.track("Investor Login");
  }

  function identifyUseronSentry(user: user) {
    !_.isNil(user.userEmail) && Sentry.setUser({ email: user.userEmail });
    Sentry.setContext("User", user as unknown as { [key: string]: unknown });
  }

  useEffect(() => {
    if (
      session &&
      session.data &&
      session.data.user &&
      session.status === "authenticated"
    ) {
      identifyUserOnSegment(session.data.user);
      identifyUseronSentry(session.data.user);
    }
  }, [session]);

  const onSubmitLogin = async (data: any) => {
    setIsFetching(true);

    try {
      let token = "default";
      if (IS_RECAPTCHA_ENABLED) {
        // Get recaptcha token
        if (!recaptchaRef.current) throw "reCaptcha null";
        token = (await recaptchaRef.current?.executeAsync()) ?? "";
        if (!token) throw "token null";
      }

      // Authenticate email & password
      const [authRes, authError] = await authenticateUserController(
        data.email,
        data.password
      );
      if (
        authError &&
        (authError.message === "Invalid credentials" ||
          authError.message === "Unable to authenticate user.")
      ) {
        useLoginFormProps.setError(
          "email",
          {
            type: "custom",
            message: "Invalid username or password, please try again.",
          },
          { shouldFocus: true }
        );
        useLoginFormProps.setError(
          "password",
          {
            type: "custom",
            message: "Invalid username or password, please try again.",
          },
          { shouldFocus: true }
        );
        setIsFetching(false);
        recaptchaRef.current?.reset();
        return;
      }

      // Request OTP
      const [_, error] = await authGetOtpController(
        data.email,
        "login",
        token,
        currentCountry
      );
      // If email is not found, still show OTP form
      if (error && error.message !== "Not Found") {
        useLoginFormProps.setError(
          "email",
          { type: "custom", message: error.message + "" },
          { shouldFocus: true }
        );
        setIsFetching(false);
        recaptchaRef.current?.reset();
        return;
      }

      // Save info on localStorage to make the otp expiration time persistent
      // when the page is refreshed or the user tries to log in again in another tab
      const otpCountdownData = {
        email: data.email,
        otpSentAt: new Date().toJSON(),
        action: "login",
        currentCountry,
      };
      localStorage.setItem(
        "otpCountdownData",
        JSON.stringify(otpCountdownData)
      );

      // Show OTP form
      setEmail(data.email);
      setShouldShowOtpForm(true);
    } catch (error) {
      useLoginFormProps.setError(
        "email",
        { type: "custom", message: error + "" },
        { shouldFocus: true }
      );
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } finally {
      setIsFetching(false);
    }
  };

  const onSubmitOtp = async (data: any) => {
    setIsFetching(true);

    // Process login
    const result = await signIn("login", {
      email,
      otp: data.code,
      country: currentCountry,
      redirect: false,
      callbackUrl: decodeURIComponent(callbackUrl || "/terms-and-conditions"),
    });

    // If login process fail, show error to user
    if (result && result.ok === false) {
      const errorMessage = result.error ?? "An error occurred"; // Use a default value if result.error is null
      useOtpFormProps.setError(
        "code",
        { type: "custom", message: errorMessage },
        { shouldFocus: true }
      );
      setIsFetching(false);
      return;
    }

    // Redirect on successful signup
    setIsLoginSuccess(true);
    if (result && result.url) {
      localStorage.removeItem("otpCountdownData");
      sessionStorage.removeItem("selectedCountry");
      router.push(result.url!);
    }
  };

  // If diff between current time & localStorage time is < 5 mins show otp form
  useEffect(() => {
    const storedDataString = localStorage.getItem("otpCountdownData");
    if (storedDataString) {
      const storedData = JSON.parse(storedDataString);
      const email = storedData.email;
      const action = storedData.action;
      const otpSentAt = new Date(storedData.otpSentAt);
      const now = new Date();
      const timeDifference = Number(now) - Number(otpSentAt);
      const secondsDifference = Math.floor(timeDifference / 1000);

      if (action !== "login") {
        localStorage.removeItem("otpCountdownData");
        return;
      }

      if (secondsDifference > 300) {
        localStorage.removeItem("otpCountdownData");
      } else {
        setEmail(email);
        setShouldShowOtpForm(true);
      }
    }
  }, []);

  const renderFooter = () => {
    switch (tenantConfig.entity_short_name.toLowerCase()) {
      case "alta":
        return (
          <>
            If you have any issues with signing in, please reach out via our
            Helpdesk by clicking on the icon below or contact us at{" "}
            <PrimeLink href={"mailto:" + tenantConfig.email.generic}>
              {tenantConfig.email.generic}
            </PrimeLink>
            .
          </>
        );
      default:
        return (
          <>
            If you have any issues with signing in, contact us at{" "}
            <PrimeLink href={"mailto:" + tenantConfig.email.generic}>
              {tenantConfig.email.generic}
            </PrimeLink>
            .
          </>
        );
    }
  };

  const renderPasswordResetInfo = () => {
    switch (tenantConfig.entity_short_name.toLowerCase()) {
      case "alta":
        return (
          <section
            id="login-section-info-set-password"
            className="flex z-90 p-4 mt-6 mb-4 rounded-md border-solid	border border-orange-200 bg-orange-100"
          >
            <div>
              <ExclamationCircleIcon className="text-amber-700 w-5" />
            </div>

            <div className="text-amber-700 text-sm ml-2">
              Passwords are now required in addition to existing authentication
              methods. If you&apos;re an existing{" "}
              {tenantConfig.entity_short_name} user, please{" "}
              <PrimeLink href="/reset-password">click here</PrimeLink> to set
              your password.
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  if (isLoginSuccess) {
    return (
      <>
        <Head>
          <title>Signing In - {tenantConfig.entity_short_name}</title>
        </Head>
        <AuthLayout componentId="login-auth" country={currentCountry}>
          <h1 className="font-light tracking-wide">
            Signing you into {tenantConfig.entity_short_name}
          </h1>
        </AuthLayout>
      </>
    );
  }

  const renderPlatformName = () => {
    switch (tenantConfig.entity_short_name.toLowerCase()) {
      case "dicree":
        return "ARCADE";
      default:
        return "Alta";
    }
  };

  return (
    <>
      <Head>
        <title>
          {props.title ? props.title : `Sign in - ${renderPlatformName()}`}
        </title>
      </Head>
      {featureFlag("ecf") && !isLoading && (
        <CountryNavbarComponent
          componentId="login-navbar"
          countryCode={currentCountry}
          setCountry={setCountry}
        />
      )}
      <AuthLayout componentId="login-auth" country={currentCountry}>
        {shouldShowOtpForm ? (
          <>
            <h1 className="font-light tracking-wide">
              Two-factor authentication
            </h1>
            <div id="login-verify-otp-box">
              <h2>Check your email</h2>
              <p>
                <span>Please enter OTP sent to:</span>
                <br />
                <span
                  id="login-auth-otp-text-email"
                  className="font-bold text-black"
                >
                  {email}
                </span>
              </p>
              <AuthOtpComponent
                useFormProps={useOtpFormProps}
                onSubmit={onSubmitOtp}
                isFetching={isFetching}
                componentId="login-auth-otp-form"
              />
              <p className="pt-2 text-xs leading-normal">
                You will receive an email if an account exists.
                <br />
                <span className="block mt-1"></span>
                {renderFooter()}
              </p>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-light tracking-wide">
              Sign in to {renderPlatformName()}
            </h1>
            {/* 2FA info component */}
            {renderPasswordResetInfo()}
            <div>
              <h2>Welcome back</h2>
              <p>Sign in with your {tenantConfig.entity_short_name} account</p>
              {IS_RECAPTCHA_ENABLED && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
                  badge="bottomleft"
                />
              )}
              <AuthLoginComponent
                componentId="login-form"
                onSubmit={onSubmitLogin}
                useFormProps={useLoginFormProps}
                isFetching={isFetching}
                info={info}
              />
              <div
                id="login-text-signup"
                className="mx-auto text-sm !mt-4 w-fit"
              >
                Don&apos;t have an account?{" "}
                <PrimeLink
                  id="login-text-signup-link-create-account"
                  href={
                    props.campaignCode === renew2022CampaignData.data.code
                      ? "/renew2022"
                      : "/signup"
                  }
                >
                  Sign up
                </PrimeLink>
              </div>
            </div>
          </>
        )}
      </AuthLayout>
    </>
  );
}

export default LoginPage;
